// ABOUTME: Manages theme mode and accent color state
// ABOUTME: Persists preferences to localStorage using React Context

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"

export type ThemeMode = "light" | "dark" | "system"
export type AccentColor = "amber" | "navy" | "olive" | "rose"

export interface ThemeState {
  mode: ThemeMode
  resolvedMode: "light" | "dark"
  accent: AccentColor
}

interface ThemeActions {
  setMode: (mode: ThemeMode) => void
  setResolvedMode: (mode: "light" | "dark") => void
  setAccent: (accent: AccentColor) => void
}

type ThemeContextValue = ThemeState & ThemeActions

const STORAGE_KEY = "gentleman-theme-store"

function loadPersisted(): { mode: ThemeMode; accent: AccentColor } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      const mode =
        parsed.mode === "light" || parsed.mode === "dark" || parsed.mode === "system"
          ? parsed.mode
          : "system"
      const accent =
        parsed.accent === "navy" || parsed.accent === "olive" || parsed.accent === "rose"
          ? parsed.accent
          : "amber"
      return { mode, accent }
    }
  } catch {}
  return { mode: "system", accent: "amber" }
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function ThemeStoreProvider({ children }: { children: React.ReactNode }) {
  const initial = loadPersisted()
  const [mode, setModeState] = useState<ThemeMode>(initial.mode)
  const [resolvedMode, setResolvedMode] = useState<"light" | "dark">("light")
  const [accent, setAccentState] = useState<AccentColor>(initial.accent)

  const persist = useCallback((m: ThemeMode, a: AccentColor) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ mode: m, accent: a }))
    } catch {}
  }, [])

  const setMode = useCallback(
    (m: ThemeMode) => {
      setModeState(m)
      persist(m, accent)
    },
    [accent, persist],
  )

  const setAccent = useCallback(
    (a: AccentColor) => {
      setAccentState(a)
      persist(mode, a)
      if (a === "amber") {
        document.documentElement.removeAttribute("data-accent")
      } else {
        document.documentElement.setAttribute("data-accent", a)
      }
    },
    [mode, persist],
  )

  useEffect(() => {
    if (accent !== "amber") {
      document.documentElement.setAttribute("data-accent", accent)
    }
  }, [accent])

  return (
    <ThemeContext.Provider
      value={useMemo(
        () => ({ mode, resolvedMode, accent, setMode, setResolvedMode, setAccent }),
        [mode, resolvedMode, accent, setMode, setAccent],
      )}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export function useThemeStore(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error("useThemeStore must be used within ThemeStoreProvider")
  return ctx
}
